<script setup>
import { computed } from 'vue'

import { formatTime } from '@/lib/utils'

import { useNotifications } from '@/composables/notifications'
import { signalToneEnum, signalToneColor } from '@/composables/dashboard-kpi'

import ListItem from '@/components/shared/ListItem.vue'
import NotificationSnoozeMenu from '@/components/NotificationSnoozeMenu.vue'

const DEFAULT_ICON = 'mdi-bell-outline'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  snoozable: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['click', 'toggle-read', 'snooze'])

const { notificationTypeChips, isAttentionNotification, notificationTargetIcon, notificationMessage, notificationDay } = useNotifications()

const typeChips = computed(() => notificationTypeChips(props.item))
const avatarIcon = computed(() => notificationTargetIcon(props.item) ?? DEFAULT_ICON)
const avatarColor = computed(() => (props.item.is_read ? undefined : typeChips.value[0]?.color))
</script>

<template>
  <q-item class="q-px-none">
    <list-item
      class="q-px-sm"
      dense
      clickable
      :caption-chips="typeChips"
      @click="emit('click', item)"
    >
      <template #avatar>
        <q-avatar>
          <q-icon
            :name="avatarIcon"
            size="sm"
            :color="item.is_read ? 'grey-5' : undefined"
            :style="{ color: avatarColor }"
          />
        </q-avatar>
      </template>
      <template #label>
        <q-item-label
          class="text-left"
          :class="{ 'text-weight-bold': !item.is_read }"
        >
          {{ notificationMessage(item) }}
        </q-item-label>
      </template>
      <template
        v-if="isAttentionNotification(item)"
        #caption
      >
        <q-icon
          name="mdi-alert-circle-outline"
          :color="signalToneColor(signalToneEnum.ATTENTION)"
          class="q-mr-xs"
        />
      </template>
      <template #right>
        <q-item-section
          side
          class="q-pl-sm"
        >
          <div class="row no-wrap items-center q-gutter-x-xs">
            <div class="column items-end">
              <q-item-label :class="{ 'text-primary text-weight-medium': !item.is_read }">
                {{ formatTime(item.visible_from) }}
              </q-item-label>
              <q-item-label caption class="row no-wrap items-center">
                <q-icon name="mdi-calendar" class="q-mr-xs" />
                <span>{{ notificationDay(item) }}</span>
              </q-item-label>
            </div>
            <q-btn
              flat
              round
              dense
              size="sm"
              color="grey-6"
              :icon="item.is_read ? 'mdi-email-mark-as-unread' : 'mdi-email-open-outline'"
              @click.stop="emit('toggle-read', item)"
            />
            <q-btn
              v-if="snoozable"
              flat
              round
              dense
              size="sm"
              color="grey-6"
              icon="mdi-bell-sleep-outline"
              @click.stop
            >
              <notification-snooze-menu @snooze="until => emit('snooze', item, until)" />
            </q-btn>
          </div>
        </q-item-section>
      </template>
    </list-item>
  </q-item>
</template>
